import { faEdit, faPlus } from "@fortawesome/free-solid-svg-icons";
import { DatePicker } from "antd";
import moment, { Moment } from "moment";
import { ChangeEvent, Dispatch, SetStateAction, useState } from "react";
import { useFormContext } from "react-hook-form";
import UnForm from "../../../../Components/Form";
import OrSpinner from "../../../../Components/Utils/OrSpinner";
import {
    AtualizacaoDeCustoPrevistoController,
    CadastroDeCustoPrevistoController,
    getDeCustoPrevistoController,
} from "../../../../Controllers/AssistenciaTecnica/CustoPrevistoRelatorioBI/CustoPrevistoRelatorioBIController";
import { ListarEmpreendimentosController } from "../../../../Controllers/Geral/Produtos/ProdutosControllers";
import useAsyncCallback from "../../../../hooks/useAsyncCallback";
import useAsyncEffect from "../../../../hooks/useAsyncEffect";
import {
    ICustoPrevistoVL,
    IListagemDeCustosPrevisto,
} from "../../../../Interfaces/AssistenciaTecnica/CustoPrevistoRelatorioBI/CustoPrevistoRelatorioBI";
import { formatMoneyReais } from "../../../../Tools/FormatTools";
import { MoneyReaisFieldMask } from "../../../../Validations/InputMasks";

interface ICreateUpdateCustoPrevisto {
    row?: IListagemDeCustosPrevisto;
    setShow: Dispatch<SetStateAction<boolean>>;
    ListagemDeCustosPrevisto: (
        ...args: any[]
    ) => Promise<IListagemDeCustosPrevisto[]>;
}

export default function CreateUpdateCustoPrevistoVGV(
    props: ICreateUpdateCustoPrevisto
) {
    const formMethods = useFormContext<IListagemDeCustosPrevisto>();

    const {
        execute: CadastroDeCustosPrevisto,
        loading: CadastroDeCustosPrevistoLoading,
    } = useAsyncCallback(CadastroDeCustoPrevistoController);

    const {
        execute: AtualizacaoDeCustoPrevisto,
        loading: AtualizacaoDeCustoPrevistoLoading,
    } = useAsyncCallback(AtualizacaoDeCustoPrevistoController);

    const { value: getCustoPrevisto, loading: getCustoPrevistoLoading } =
        useAsyncEffect(async () => {
            if (props.row) {
                const res = await getDeCustoPrevistoController(props.row.ID);
                return res;
            }
        }, [props.row]);

    const [ano, setAno] = useState<string>(String(getCustoPrevisto?.ANO ?? ""));

    function onChange(date: Moment | null, dateString: string) {
        setAno(dateString);
    }
    const [mes, setMes] = useState<string>(String(getCustoPrevisto?.MES ?? ""));

    function onChangeMes(date: Moment | null, dateString: string) {
        setMes(dateString);
    }

    function geradorMascaraFinanceira(inputName: keyof ICustoPrevistoVL) {
        return (e: ChangeEvent<HTMLInputElement>) => {
            const mask = MoneyReaisFieldMask(e);
            formMethods?.setValue(inputName, mask);
        };
    }

    const { value: lstEmpreendimentos, loading: lstEmpreendimentosLoading } =
        useAsyncEffect(() => ListarEmpreendimentosController(), []);

    function handleCreateUpdatePrevisto(formData: any) {
        if (props.row) {
            AtualizacaoDeCustoPrevisto([
                {
                    ...formData,
                    ID: props.row.ID,
                    CUSTOS: {
                        ID: props.row.CUSTOS.ID,
                        ID_CUSTO_PREVISTO_VGV:
                            props.row.CUSTOS.ID_CUSTO_PREVISTO_VGV,
                        VL_CUSTO_PREVISTO_1_0_PERC:
                            formData.CUSTOS.VL_CUSTO_PREVISTO_1_0_PERC,
                        VL_CUSTO_PREVISTO_1_5_PERC:
                            formData.CUSTOS.VL_CUSTO_PREVISTO_1_5_PERC,
                        VL_CUSTO_PREVISTO_1_8_PERC:
                            formData.CUSTOS.VL_CUSTO_PREVISTO_1_8_PERC,
                    },
                    MES: mes ? mes : getCustoPrevisto?.MES ?? "",
                    ANO: ano ? ano : getCustoPrevisto?.ANO ?? "",
                },
            ]).then(() => {
                props.setShow(false);
                props.ListagemDeCustosPrevisto();
            });
        } else {
            CadastroDeCustosPrevisto({
                ...formData,
                MES: mes ? mes : getCustoPrevisto?.MES ?? "",
                ANO: ano ? ano : getCustoPrevisto?.ANO ?? "",
            }).then(() => {
                props.setShow(false);
                props.ListagemDeCustosPrevisto();
            });
        }
    }

    const isLoading =
        CadastroDeCustosPrevistoLoading || AtualizacaoDeCustoPrevistoLoading;

    return (
        <OrSpinner
            loading={getCustoPrevistoLoading || lstEmpreendimentosLoading}
            spinner="capys"
        >
            <UnForm handleSubmit={handleCreateUpdatePrevisto}>
                <div className="row">
                    <div className="col-md-6">
                        <UnForm.Select
                            label="Residencial"
                            name="ID_RESIDENCIAL"
                            defaultValue={getCustoPrevisto?.ID_RESIDENCIAL}
                            registerOptions={{
                                required: "Selecione um residencial",
                            }}
                        >
                            <option></option>
                            {lstEmpreendimentos
                                ?.filter((lst) => lst.DS_ESTADO !== null)
                                ?.map((lst) => (
                                    <option
                                        key={lst.ID_EMPREENDIMENTO}
                                        value={lst.ID_EMPREENDIMENTO}
                                    >
                                        {lst.DS_EMPREENDIMENTO}
                                    </option>
                                ))}
                        </UnForm.Select>
                    </div>

                    <div className="col-md-6">
                        <UnForm.Input
                            label="Valor 1.0%"
                            name="CUSTOS.VL_CUSTO_PREVISTO_1_0_PERC"
                            registerOptions={{
                                setValueAs: (v: string) => {
                                    return Number(
                                        MoneyReaisFieldMask.unMask(v)
                                    );
                                },
                                required: "Insira um valor",
                            }}
                            onChange={geradorMascaraFinanceira(
                                "VL_CUSTO_PREVISTO_1_8_PERC"
                            )}
                            defaultValue={formatMoneyReais(
                                getCustoPrevisto?.CUSTOS
                                    .VL_CUSTO_PREVISTO_1_0_PERC ?? 0
                            )}
                        />
                    </div>

                    <div className="col-md-6">
                        <UnForm.Input
                            label="Valor 1.5%"
                            name="CUSTOS.VL_CUSTO_PREVISTO_1_5_PERC"
                            registerOptions={{
                                setValueAs: (v: string) => {
                                    return Number(
                                        MoneyReaisFieldMask.unMask(v)
                                    );
                                },
                                required: "Insira um valor",
                            }}
                            onChange={geradorMascaraFinanceira(
                                "VL_CUSTO_PREVISTO_1_5_PERC"
                            )}
                            defaultValue={formatMoneyReais(
                                getCustoPrevisto?.CUSTOS
                                    .VL_CUSTO_PREVISTO_1_5_PERC ?? 0
                            )}
                        />
                    </div>

                    <div className="col-md-6">
                        <UnForm.Input
                            label="Valor 1.8%"
                            name="CUSTOS.VL_CUSTO_PREVISTO_1_8_PERC"
                            registerOptions={{
                                setValueAs: (v: string) => {
                                    return Number(
                                        MoneyReaisFieldMask.unMask(v)
                                    );
                                },
                                required: "Insira um valor",
                            }}
                            onChange={geradorMascaraFinanceira(
                                "VL_CUSTO_PREVISTO_1_8_PERC"
                            )}
                            defaultValue={formatMoneyReais(
                                getCustoPrevisto?.CUSTOS
                                    .VL_CUSTO_PREVISTO_1_8_PERC ?? 0
                            )}
                        />
                    </div>

                    <div className="col-md-6">
                        <label>Mês</label>
                        <DatePicker
                            picker="month"
                            onChange={onChangeMes}
                            className="form-control"
                            defaultValue={
                                getCustoPrevisto?.MES
                                    ? moment(
                                          getCustoPrevisto?.MES.toString(),
                                          "MM"
                                      )
                                    : undefined
                            }
                            placeholder=""
                            format={"MM"}
                        />
                    </div>
                    <div className="col-md-6">
                        <label>Ano</label>
                        <DatePicker
                            picker="year"
                            onChange={onChange}
                            className="form-control"
                            defaultValue={
                                getCustoPrevisto?.ANO
                                    ? moment(
                                          getCustoPrevisto?.ANO.toString(),
                                          "YYYY"
                                      )
                                    : undefined
                            }
                            placeholder=""
                        />
                    </div>
                    <div className="col-md-12">
                        <UnForm.SubmitButton
                            className="w-100 mt-5"
                            type="submit"
                            color={props.row ? "primary" : "success"}
                            icon={props.row ? faEdit : faPlus}
                            wrapperClassName="w-100"
                            loading={isLoading}
                            disabled={isLoading}
                            loadingSize={"sm"}
                        >
                            {props.row ? "Editar" : "Salvar"}
                        </UnForm.SubmitButton>
                    </div>
                </div>
            </UnForm>
        </OrSpinner>
    );
}
